import { useEffect, useState } from 'react';
import { Plug, AlertCircle, Plus, Wifi, RefreshCw, Trash2 } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { erpApi, ApiError } from '@solara/api-client';
import type { ErpConnectorFullDto, ErpVendorMeta } from '@solara/api-client';

const STATUS_TONES: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  CONNECTED: 'success',
  ERROR: 'danger',
  DISCONNECTED: 'warning',
};

export default function ErpConnectorsPage() {
  const { t } = useTranslation('p1');
  const [vendors, setVendors] = useState<ErpVendorMeta[]>([]);
  const [connectors, setConnectors] = useState<ErpConnectorFullDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ vendor: string; name: string; config: Record<string, string> } | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [v, c] = await Promise.all([erpApi.vendors(), erpApi.list()]);
      setVendors(v);
      setConnectors(c);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  function startWizard(vendor: ErpVendorMeta) {
    const config: Record<string, string> = {};
    for (const f of vendor.fields) config[f] = '';
    setDraft({ vendor: vendor.vendor, name: `${vendor.label} — pilot`, config });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await erpApi.create(draft);
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function testConn(id: string) {
    setBusy(true);
    try {
      await erpApi.test(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('erp.testFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function syncConn(id: string) {
    setBusy(true);
    try {
      const result = await erpApi.sync(id);
      await load();
      alert(t('erp.synced', { count: result.synced }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('erp.syncFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('erp.confirmRemove'))) return;
    setBusy(true);
    try {
      await erpApi.remove(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('erp.removeFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('erp.title')}</h1>
        <p className="text-fg-muted text-sm">{t('erp.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-4 w-4" />
            {t('erp.available')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {vendors.map((v) => (
              <li key={v.vendor} className="flex items-center justify-between rounded border border-border bg-surface px-3 py-2 text-sm">
                <div>
                  <p className="font-medium">{v.label}</p>
                  <p className="text-[10px] text-fg-muted">
                    {t('erp.configFields', { count: v.fields.length })}
                  </p>
                </div>
                <Button variant="ghost" onClick={() => startWizard(v)}>
                  <Plus className="mr-1 h-3 w-3" />
                  {t('erp.add')}
                </Button>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      {draft && (
        <Card className="rounded-2xl border-brand-500/30">
          <CardHeader>
            <CardTitle>
              {t('erp.configure', {
                label: vendors.find((v) => v.vendor === draft.vendor)?.label ?? draft.vendor,
              })}
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <Input
              label={t('erp.connectorName')}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            {Object.keys(draft.config).map((key) => (
              <Input
                key={key}
                label={key}
                type={/secret|password|token/i.test(key) ? 'password' : 'text'}
                value={draft.config[key]}
                onChange={(e) => setDraft({ ...draft, config: { ...draft.config, [key]: e.target.value } })}
              />
            ))}
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy}>
                {busy ? t('actions.saving') : t('erp.saveConnector')}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('erp.activeConnectors', { count: connectors.length })}</CardTitle>
        </CardHeader>
        <CardBody>
          {connectors.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('erp.noConnectorsHint')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {connectors.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-fg-muted">
                      {c.vendor}
                      {c.lastSyncAt && (
                        <span>
                          {' '}
                          · {t('erp.lastSync')} {new Date(c.lastSyncAt).toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={STATUS_TONES[c.status] ?? 'neutral'} className="text-[10px]">
                      {t(`erp.statuses.${c.status}`, { defaultValue: c.status })}
                    </Badge>
                    <Button variant="ghost" onClick={() => void testConn(c.id)} disabled={busy}>
                      <Wifi className="mr-1 h-3 w-3" />
                      {t('erp.test')}
                    </Button>
                    <Button variant="ghost" onClick={() => void syncConn(c.id)} disabled={busy || c.status !== 'CONNECTED'}>
                      <RefreshCw className="mr-1 h-3 w-3" />
                      {t('erp.sync')}
                    </Button>
                    <Button variant="ghost" onClick={() => void remove(c.id)} disabled={busy}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
